import { useCallback} from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  resetError,
  logoutAsync,
  loginWithEmailAsync,
  registerWithEmailAsync,
  resetPasswordAsync,
  updateProfileAsync,
  loginWithGoogleAsync,
  loginWithFacebookAsync,
  loginWithTwitterAsync,
  loginWithAppleAsync
} from '../store/slices/authSlice';
import { 
  RegisterUserData,
  UserProfile
} from '../services/firebase/auth.service';

// Removed global auth timeout flag - not needed

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, isInitialized, isLoading, error } = useAppSelector(
    (state) => state.auth
  );
  
  
  // Auth state is handled by auth-navigation.ts globally

  // Login with email/username and password
  const loginWithEmail = useCallback(
    async (emailOrUsername: string, password: string) => {
      return dispatch(loginWithEmailAsync({ email: emailOrUsername, password })).unwrap();
    },
    [dispatch]
  );

  // Register with email
  const registerWithEmail = useCallback(
    async (userData: RegisterUserData) => {
      return dispatch(registerWithEmailAsync(userData)).unwrap();
    },
    [dispatch]
  );

  // Logout
  const logoutUser = useCallback(async () => {
    return dispatch(logoutAsync()).unwrap();
  }, [dispatch]);

  // Reset password
  const resetUserPassword = useCallback(
    async (email: string) => {
      return dispatch(resetPasswordAsync(email)).unwrap();
    },
    [dispatch]
  );

  // Update profile
  const updateProfile = useCallback(
    async (uid: string, profileData: Partial<UserProfile>) => {
      return dispatch(updateProfileAsync({ uid, profileData })).unwrap();
    },
    [dispatch]
  );

  // Social logins
  const loginWithGoogle = useCallback(
    async (useRedirect: boolean = false) => {
      return dispatch(loginWithGoogleAsync(useRedirect)).unwrap();
    },
    [dispatch]
  );

  const loginWithFacebook = useCallback(
    async (useRedirect: boolean = false) => {
      return dispatch(loginWithFacebookAsync(useRedirect)).unwrap();
    },
    [dispatch]
  );

  const loginWithTwitter = useCallback(
    async (useRedirect: boolean = false) => {
      return dispatch(loginWithTwitterAsync(useRedirect)).unwrap();
    },
    [dispatch]
  );

  const loginWithApple = useCallback(
    async (useRedirect: boolean = false) => {
      return dispatch(loginWithAppleAsync(useRedirect)).unwrap();
    },
    [dispatch]
  );

  // Clear error
  const clearError = useCallback(() => {
    dispatch(resetError());
  }, [dispatch]);

  return {
    user,
    isAuthenticated,
    isInitialized,
    isLoading,
    error,
    loginWithEmail,
    registerWithEmail,
    logoutUser,
    resetUserPassword,
    updateProfile,
    loginWithGoogle,
    loginWithFacebook,
    loginWithTwitter,
    loginWithApple,
    clearError
  };
};